import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import Plausible from 'plausible-tracker';
import TagManager from 'react-gtm-module';
import * as z from 'zod';

import { MatomoTracker } from '@canvasflow/analytics';
import type { UserOptions as MatomoOptions } from '@canvasflow/analytics';

import { TrackingContext, LOCATION_CHANNEL_KEY } from './tracking.context';
import config from '@/config';
import type { Analytics } from '@shared/config';
import {
	TrackingEvent,
	TrackerLogger,
	TrackerHandles,
} from './tracking.events';
import { Trackers } from './trackers';
import { MetaPixelTracker } from './trackers/meta-pixel-tracker';
import { APP_USER_ID } from '@/constants';
import { TiktokPixelTracker } from './trackers/tiktok-pixel-tracker';
import { SegmentTracker, SEGMENT_WRITE_KEY } from './trackers/segment-tracker';

const ANALYTICS_ENABLED_KEY = 'analytics_enabled';
const ANALYTICS_CHANNEL_KEY = 'analytics';
const ANALYTICS_ENABLED = 'enable';
const ANALYTICS_DISABLE = 'disable';

// The analytics BroadcastChannel also carries `TrackingEvent` payloads
// (see `sendEvent` below) alongside the enable/disable control strings --
// any tab with the app open can post to it, so the listener below can't
// trust `data`'s shape without checking. Only the control strings need
// validating here since they're the only messages this listener acts on.
const AnalyticsControlMessageSchema = z.union([
	z.literal(ANALYTICS_ENABLED),
	z.literal(ANALYTICS_DISABLE),
]);

declare global {
	// Set by third-party scripts injected via pwa.json's preBody/postBody
	// (OneTrust's consent-management script, an A360-specific targeting
	// cookie flag) -- never read from our own code, only declared so a
	// stray reference from an inline script doesn't trip a "not defined"
	// error. Shape is whatever that external script defines, hence `unknown`
	// rather than a guessed structure.
	const OnetrustActiveGroups: unknown;
	const a360_targetting_cookies: unknown;
	interface Window {
		a360_targetting_cookies: unknown;
		// The real gtag.js function: first arg is the command name
		// ('event', 'config', 'consent', ...), the rest vary by command --
		// see GtagTracker.ts's real call sites for the shapes actually used.
		// Optional: not present until the gtag/GTM script finishes loading
		// (see initializeGtag's poll below).
		gtag?: (command: string, ...args: unknown[]) => void;
		// Matomo's command queue -- pushed to directly by the `_paq.push(...)`
		// snippet OneTrust/Matomo's own script injects; each entry is a
		// command name followed by variadic, command-specific arguments.
		_paq: [string, ...unknown[]][];
	}
}

export function Tracking({ children }: TrackingProps) {
	const { analytics } = config;
	const [gtag, setGtag] = useState<TrackerHandles['gtag']>(null);
	const [gtm, setGTM] = useState<TrackerHandles['gtm']>(null);
	const [plausible, setPlausible] =
		useState<TrackerHandles['plausible']>(null);
	const [matomo, setMatomo] = useState<TrackerHandles['matomo']>(null);
	const [metaPixel, setMetaPixel] =
		useState<TrackerHandles['metaPixel']>(null);
	const [tiktokPixel, setTiktokPixel] =
		useState<TrackerHandles['tiktokPixel']>(null);
	const [segment, setSegment] = useState<TrackerHandles['segment']>(null);
	// Lazy initialisers. Without the arrow, `useState(new BroadcastChannel(...))`
	// constructs a *new* channel on every single render of this provider and
	// throws it away -- the argument is evaluated before React decides to
	// ignore it. Each discarded channel stays open (nothing closes it), so
	// the app accumulated live BroadcastChannels for as long as it ran. With
	// the arrow, exactly one of each is ever created.
	const [locationChannel] = useState(
		() => new BroadcastChannel(LOCATION_CHANNEL_KEY),
	);
	const [analyticsChannel] = useState(
		() => new BroadcastChannel(ANALYTICS_CHANNEL_KEY),
	);
	// Deliberately never `close()`d on unmount. These two are app-lifetime
	// singletons owned by the root provider, which only unmounts when the
	// document goes away and the browser reclaims them anyway. Closing them
	// from an effect cleanup would actively break under StrictMode's
	// mount -> unmount -> remount: `useState` preserves the channel across
	// that cycle, so the second mount would reuse an already-closed channel
	// and every postMessage would throw. The leak worth fixing here was the
	// per-render construction above, not the absence of a close().

	// setGtag/setGTM/etc. are useState setters -- React guarantees their
	// identity is stable across renders -- and `analytics` comes from the
	// static build config, so this object's own dependencies never actually
	// change. Memoized so its reference stays stable too, otherwise the
	// mount-time effect below would re-run (re-initializing every tracker)
	// on every unrelated re-render, since a fresh object literal would
	// never satisfy a dependency comparison.
	const trackerOptions = useMemo(
		() => ({
			analytics,
			setGtag,
			setGTM,
			setMatomo,
			setPlausible,
			setMetaPixel,
			setTiktokPixel,
			setSegment,
		}),
		[
			analytics,
			setGtag,
			setGTM,
			setMatomo,
			setPlausible,
			setMetaPixel,
			setTiktokPixel,
			setSegment,
		],
	);
	useEffect(() => {
		initializeTrackers(trackerOptions);
		// TODO this is only aplicable for A360
		// if (localStorage.getItem(ANALYTICS_ENABLED_KEY)) {
		// 	initializeTrackers(trackerOptions);
		// }
		const onMessage = ({ data }: MessageEvent) => {
			const result = AnalyticsControlMessageSchema.safeParse(data);
			if (!result.success) {
				return;
			}
			const message = result.data;

			if (
				message === ANALYTICS_DISABLE &&
				localStorage.getItem(ANALYTICS_ENABLED_KEY)
			) {
				disableTrackers(trackerOptions);
				// location.reload();
				return;
			}
			if (
				message === ANALYTICS_ENABLED &&
				!localStorage.getItem(ANALYTICS_ENABLED_KEY)
			) {
				initializeTrackers(trackerOptions);
				// location.reload();
				return;
			}
		};

		// addEventListener rather than the `onmessage` property, so this can
		// actually be torn down. The effect re-runs when `trackerOptions`
		// changes, and the old assignment left the previous handler -- with
		// its stale `trackerOptions` closure -- in place until it happened to
		// be overwritten.
		analyticsChannel.addEventListener('message', onMessage);
		return () => {
			analyticsChannel.removeEventListener('message', onMessage);
		};
	}, [trackerOptions, analyticsChannel]);

	const trackers: Trackers = useMemo(() => {
		return new Trackers({
			gtag,
			gtm,
			matomo,
			plausible,
			metaPixel,
			tiktokPixel,
			segment,
		});
	}, [gtag, gtm, matomo, plausible, metaPixel, tiktokPixel, segment]);

	const sendEvent = useCallback(
		(event: TrackingEvent) => {
			trackers.sendEvent(event);
			analyticsChannel.postMessage(event);
		},
		[trackers, analyticsChannel],
	);

	// Consumed by 28 components -- menu buttons, replica single/double views,
	// every home screen -- so an inline object literal here re-rendered
	// effectively the whole app whenever this provider re-rendered, even
	// though the tracker handles themselves rarely change after boot.
	const value = useMemo(
		() => ({
			gtag,
			gtm,
			plausible,
			locationChannel,
			matomo,
			sendEvent,
			metaPixel,
			tiktokPixel,
			segment,
		}),
		[
			gtag,
			gtm,
			plausible,
			locationChannel,
			matomo,
			sendEvent,
			metaPixel,
			tiktokPixel,
			segment,
		],
	);

	return (
		<TrackingContext.Provider value={value}>
			{children}
		</TrackingContext.Provider>
	);
}

function initializeTrackers(args: TrackersOptions) {
	const {
		analytics,
		setGtag,
		setGTM,
		setPlausible,
		setMatomo,
		setMetaPixel,
		setTiktokPixel,
		setSegment,
	} = args;
	TrackerLogger.info(`Initialize trackers: \n`, analytics);
	if (analytics?.gtag?.trackerID) {
		initializeGtag(setGtag);
	}

	if (analytics?.gtm?.trackerID) {
		initializeGTM(setGTM);
	}

	if (analytics?.plausible?.domain) {
		initializePlausible(setPlausible);
	}

	if (analytics?.metaPixel?.trackerID) {
		initializeMetaPixel(setMetaPixel);
	}

	if (analytics?.tiktokPixel?.trackerID) {
		initializeTiktokPixel(setTiktokPixel);
	}

	const matomoConfig = analytics?.matomo as
		Partial<MatomoOptions> | undefined;
	if (matomoConfig?.srcUrl && matomoConfig?.urlBase) {
		initializeMatomo(setMatomo);
	}

	// TODO: Gate on `analytics?.segment?.writeKey` once the write key is
	// provided through the configuration object. For now it falls back to the
	// hardcoded key in SegmentTracker.
	initializeSegment(setSegment);
	localStorage.setItem(ANALYTICS_ENABLED_KEY, 'true');
}

function disableTrackers(args: TrackersOptions) {
	const {
		setGtag,
		setGTM,
		setMatomo,
		setPlausible,
		setMetaPixel,
		setTiktokPixel,
		setSegment,
	} = args;
	TrackerLogger.info(`I disable all the analytics`);
	setGtag(null);
	setGTM(null);
	setMatomo(null);
	setPlausible(null);
	setMetaPixel(null);
	setTiktokPixel(null);
	setSegment(null);
	localStorage.removeItem(ANALYTICS_ENABLED_KEY);
}

interface TrackersOptions {
	analytics: Analytics | undefined;
	setGtag: (opts: TrackerHandles['gtag']) => void;
	setGTM: (opts: TrackerHandles['gtm']) => void;
	setMatomo: (opts: TrackerHandles['matomo']) => void;
	setPlausible: (opts: TrackerHandles['plausible']) => void;
	setMetaPixel: (opts: TrackerHandles['metaPixel']) => void;
	setTiktokPixel: (opts: TrackerHandles['tiktokPixel']) => void;
	setSegment: (opts: TrackerHandles['segment']) => void;
}

interface TrackingProps {
	children: string | JSX.Element | JSX.Element[];
}

function initializeGtag(setter: (gtag: TrackerHandles['gtag']) => void) {
	/*let sampleRate = 1;
	if (
		!!analytics.ga4.sampleRate &&
		typeof analytics.ga4.sampleRate === 'number'
	) {
		if (analytics.ga4.sampleRate > 0 && analytics.ga4.sampleRate < 100) {
			sampleRate = analytics.ga4.sampleRate;
		}
	}*/

	if (window.gtag) {
		setter(true);
		return;
	}

	// Poll for window.gtag to become available (the GTM/gtag script loads
	// async), but don't do it forever -- give up after a bounded wait
	// instead of polling for the lifetime of the session if the script
	// never loads (network failure, ad blocker, etc).
	const MAX_GTAG_WAIT = 10000;
	const interval = setInterval(() => {
		if (window.gtag) {
			clearInterval(interval);
			clearTimeout(giveUp);
			setter(true);
		}
	}, 500);
	const giveUp = setTimeout(() => {
		clearInterval(interval);
	}, MAX_GTAG_WAIT);
}

function initializeGTM(setter: (gtm: TrackerHandles['gtm']) => void) {
	const { analytics } = config;
	const gtmId = analytics?.gtm?.trackerID;
	// The call site (initializeTrackers) already gates this call on the same
	// check; guarded again here since TagManagerArgs.gtmId is required.
	if (!gtmId) return;

	const tagManagerArgs = { gtmId };

	TagManager.initialize(tagManagerArgs);
	setter(TagManager);
	return;
}

function initializePlausible(
	setter: (plausible: TrackerHandles['plausible']) => void,
) {
	const { analytics } = config;
	const domain = analytics?.plausible?.domain;

	const plausible = Plausible({
		domain: domain,
		trackLocalhost: true,
	});

	setter(plausible);
	return;
}

function initializeMetaPixel(
	setter: (metaPixel: TrackerHandles['metaPixel']) => void,
) {
	const { analytics } = config;
	const pixelId = analytics?.metaPixel?.trackerID;
	if (!pixelId) return;

	MetaPixelTracker.init(pixelId);

	setter(pixelId);
	return;
}

function initializeTiktokPixel(
	setter: (tiktokPixel: TrackerHandles['tiktokPixel']) => void,
) {
	const { analytics } = config;
	const pixelId = analytics?.tiktokPixel?.trackerID;
	if (!pixelId) return;

	TiktokPixelTracker.init(pixelId);

	setter(pixelId);
	return;
}

function initializeSegment(
	setter: (segment: TrackerHandles['segment']) => void,
) {
	const { analytics } = config;
	const writeKey = analytics?.segment?.writeKey || SEGMENT_WRITE_KEY;
	if (!writeKey) return;

	SegmentTracker.init(writeKey);

	setter(writeKey);
	return;
}

function initializeMatomo(setter: (matomo: TrackerHandles['matomo']) => void) {
	const userId = localStorage.getItem(APP_USER_ID) || undefined;
	const { analytics } = config;
	const {
		urlBase,
		siteId,
		trackerUrl,
		srcUrl,
		disabled,
		heartBeat,
		linkTracking = true,
		configurations = {},
	} = (analytics?.matomo as Partial<MatomoOptions> | undefined) ?? {};
	// The call site (initializeTrackers) already gates this call on
	// `analytics?.matomo?.srcUrl && analytics?.matomo?.urlBase`, but
	// `siteId` isn't part of that check even though MatomoTracker's
	// constructor requires it -- guard here too rather than construct with
	// an undefined siteId (previously silently passed through as `any`).
	if (!urlBase || siteId === undefined) return;

	const matomo = new MatomoTracker({
		urlBase,
		siteId,
		trackerUrl,
		srcUrl,
		disabled,
		heartBeat,
		linkTracking,
		configurations,
		userId,
	});

	setter(matomo);
}
